import { ActorRefFrom, assign, createMachine, emit, EventFromLogic, sendParent, SnapshotFrom } from 'xstate'

import { Persona } from '@/state/AppConfig'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleEntity, CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { ShoppingMode } from '@/webgl/runner/entities/ShoppingEntity'
import { SequenceCompletion } from '@/webgl/runner/scenarios/SequenceCompletion'

import { AppExitRunnerEvent } from './AppEvent'

export type ShoppingState = {
  walletValue: number
  cart: CollectibleEntity[]
  cartValue: number
  freezeActive: boolean
  mode: ShoppingMode
}

export type RunnerContext = {
  loaded: boolean
  loadprogress: number
  scenario: number
  persona: Persona
  game: GameState
  shopping: ShoppingState
}

export type GameState = {
  paused: boolean
  popin: string
  popinAudio: string
  tooltip?: string | null
  completion: SequenceCompletion
  jauge: number
  turningPoint: string
  obstacle: string
  currentSegment: string
  currentSegmentType: string
  currentLane: number
  activeBonuses: BonusType[]
  collectStatus: CollectStatus
  loadingProgress: number
}

function CreateContext(): RunnerContext {
  return {
    loaded: false,
    loadprogress: 0,
    scenario: 0,
    persona: 'pedro',
    shopping: null,
    game: {
      paused: false,
      popin: null,
      popinAudio: null,
      tooltip: null,
      completion: null,
      jauge: 0,
      turningPoint: null,
      obstacle: null,
      currentSegment: 'loading',
      currentSegmentType: '',
      currentLane: 0,
      activeBonuses: [],
      collectStatus: CollectStatus.Neutral,
      loadingProgress: 0,
    },
  }
}

export type RunnerStartEvent = { type: 'START'; scenario: number; persona: Persona }
export type RunnerLoadedEvent = { type: 'LOADED' }
export type RunnerStopEvent = { type: 'STOP' }
export type RunnerLoadProgressEvent = { type: 'LOAD_PROGRESS'; progress: number }
export type RunnerUpdateGameStateEvent = { type: 'GAME_STATE'; value: Partial<GameState> }
export type RunnerResumeEvent = { type: 'RESUME' }
export type RunnerPauseEvent = { type: 'PAUSE' }
export type RunnerTogglePauseEvent = { type: 'TOGGLE_PAUSE' }
export type RunnerOpenPopin = { type: 'OPEN_POPIN'; value: string }
export type RunnerClosePopin = { type: 'CLOSE_POPIN' }

export type RunnerShoppingEvent = { type: 'SHOPPING_STATE'; value: Partial<ShoppingState> }

export type RunnerPlayerMoveEvent = { type: 'PLAYER_MOVE'; direction: number }

export function CreateRunnerStateMachine() {
  return createMachine({
    types: {
      events: {} as
        | RunnerStartEvent
        | RunnerLoadedEvent
        | RunnerStopEvent
        | RunnerLoadProgressEvent
        | RunnerUpdateGameStateEvent
        | RunnerResumeEvent
        | RunnerOpenPopin
        | RunnerClosePopin
        | RunnerPauseEvent
        | RunnerTogglePauseEvent
        | RunnerShoppingEvent
        | RunnerPlayerMoveEvent,
    },

    id: 'runner',

    context: CreateContext(),

    type: 'compound',

    initial: 'idle',

    on: {
      GAME_STATE: {
        actions: assign(({ context, event }) => {
          return {
            ...context,
            game: {
              ...context.game,
              ...event.value,
            },
          }
        }),
      },
      SHOPPING_STATE: {
        actions: assign(({ context, event }) => {
          return {
            ...context,
            shopping: {
              ...context.shopping,
              ...event.value,
            },
          }
        }),
      },
      STOP: {
        target: '.idle',
        actions: [
          () => console.log('[RunnerStateMachine] running:stop'),
          assign(({ context }) => CreateContext()),
          sendParent({ type: 'EXIT_RUNNER' } as AppExitRunnerEvent),
        ],
      },
      RESUME: {
        actions: assign(({ context }) => ({ ...context, game: { ...context.game, paused: false } })),
      },
      PAUSE: {
        actions: assign(({ context }) => ({ ...context, game: { ...context.game, paused: true } })),
      },
      TOGGLE_PAUSE: {
        actions: assign(({ context }) => ({ ...context, game: { ...context.game, paused: !context.game.paused } })),
      },
    },

    states: {
      idle: {
        // entry: () => console.log('[RunnerStateMachine] idle'),
        on: {
          START: {
            actions: assign(({ context, event }) => {
              return {
                ...context,
                loaded: false,
                scenario: event.scenario,
                persona: event.persona as Persona,
              }
            }),
            target: 'loading',
          },
        },
      },

      loading: {
        // entry: () => console.log('[RunnerStateMachine] loading'),
        on: {
          LOADED: {
            actions: [assign({ loaded: true })],
            target: 'running',
          },
        },
      },

      running: {
        // entry: () => console.log('[RunnerStateMachine] running'),

        on: {
          PLAYER_MOVE: {
            actions: emit(({ event }) => {
              return { type: 'player_move', direction: event.direction }
            }),
          },

          OPEN_POPIN: {
            target: 'popin',
            actions: assign(({ context, event }) => {
              return {
                ...context,
                game: {
                  ...context.game,
                  popin: event.value,
                },
              }
            }),
          },
        },
      },

      popin: {
        // entry: () => console.log('[RunnerStateMachine] popin'),

        on: {
          CLOSE_POPIN: {
            target: 'running',
            actions: assign(({ context, event }) => {
              return {
                ...context,
                game: {
                  ...context.game,
                  popin: null as string,
                },
              }
            }),
          },
        },
      },
    },
  })
}

export type RunnerStateMachine = ReturnType<typeof CreateRunnerStateMachine>

export type RunnerEvent = EventFromLogic<RunnerStateMachine>

export type RunnerActor = ActorRefFrom<RunnerStateMachine>

export type RunnerSnapshot = SnapshotFrom<RunnerStateMachine>
